import styled from '@emotion/styled';
import { ReactNode } from 'react';

import OuraWordmark from '../../images/logos/Oura.svg';
import OuraBackgroundLogo from '../../images/logos/Oura_Logo.svg?raw';
import { t } from '../localization/LocaleContext';
import Copyright from './AccountPage/components/Copyright';

const RootContainer = styled.div`
  background-color: #f6f3ef;

  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1430px;
  padding: 0 ${({ theme }) => theme.spacers.m};

  ${({ theme }) => theme.mediaQuery.md} {
    padding: 0 80px;
  }

  ${({ theme }) => theme.mediaQuery.lg} {
    padding: 0 160px;
  }
`;

const LogoContainer = styled.header`
  padding: ${({ theme }) => `${theme.spacers.l} 0 ${theme.spacers.l}`};

  ${({ theme }) => theme.mediaQuery.md} {
    padding: ${({ theme }) => `${theme.spacers.xl} 0`};
  }
`;

const OuraLogo = styled(OuraWordmark)`
  max-width: 58px;

  ${({ theme }) => theme.mediaQuery.md} {
    max-width: 80px;
  }
`;

const Divider = styled.hr`
  border-top: 1px solid ${({ theme }) => theme.colors.monochrome40};
  margin: 0;
  width: 100%;
`;

const Background = styled.div`
  width: 100%;
  flex-grow: 1;
  background-image: url(${OuraBackgroundLogo});
  background-repeat: no-repeat;
  background-position: right -270px top ${({ theme }) => theme.elements.topBarMobileHeight};
  background-size: auto
    calc(100% - ${({ theme }) => theme.elements.topBarDesktopHeight} - 4rem);

  ${({ theme }) => theme.mediaQuery.lg} {
    background-position: right -80px top ${({ theme }) => theme.elements.topBarDesktopHeight};
    background-size: auto
      calc(100% - ${({ theme }) => theme.elements.topBarDesktopHeight} - 4rem);
  }
`;

interface Props {
  children: ReactNode;
}

export default function UnAuthenticatedBase({ children }: Props) {
  return (
    <RootContainer>
      <Background>
        <ContentContainer style={{ flexGrow: 1 }}>
          <LogoContainer>
            <OuraLogo />
          </LogoContainer>
          <main>{children}</main>
        </ContentContainer>
      </Background>
      <Divider />
      <ContentContainer>
        <Footer />
      </ContentContainer>
    </RootContainer>
  );
}

const FooterContainer = styled.footer`
  ${({ theme }) => theme.textStyles.labelS};
  color: ${({ theme }) => theme.colors.blueDark40};

  a {
    ${({ theme }) => theme.textStyles.labelS};
    color: ${({ theme }) => theme.colors.blueDark40};
  }

  padding: ${({ theme }) => `${theme.spacers.s} 0 ${theme.spacers.m}`};

  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mediaQuery.md} {
    ${({ theme }) => theme.textStyles.labelL};

    a {
      ${({ theme }) => theme.textStyles.labelL};
    }

    padding: ${({ theme }) => `${theme.spacers.m} 0 ${theme.spacers.l}`};

    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;

const Links = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-bottom: ${({ theme }) => theme.spacers.s};

  ${({ theme }) => theme.mediaQuery.md} {
    max-width: 380px;
    justify-content: flex-end;
    flex-wrap: wrap;

    & > div {
      margin-left: ${({ theme }) => theme.spacers.m};
      margin-bottom: ${({ theme }) => theme.spacers.xs};
    }

    a {
      ${({ theme }) => theme.textStyles.labelLEmphasized};
    }
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <Links>
        <div>
          <a href="https://ouraring.com/terms-and-conditions">
            {t('terms_and_conditions')}
          </a>
        </div>
        <div>
          <a href="https://ouraring.com/privacy-policy">
            {t('privacy_policy')}
          </a>
        </div>
        <div>
          <a href="https://ouraring.com/intellectual-property-notice">
            {t('intellectual_property_notice')}
          </a>
        </div>
        <div>
          <a href="https://ouraring.com/accessibility">{t('accessibility')}</a>
        </div>
      </Links>
      <Copyright size="large" />
    </FooterContainer>
  );
}
